import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChatMessage, ChatHistory } from '../../types';

interface ChatState {
  messages: ChatMessage[];
  chatHistory: ChatHistory[];
  currentSessionId: string | null;
  isTyping: boolean;
  streamingMessage: string;
  userProfile: { id: string; name: string } | null;
  jobNumber: string | null;
  uploadedFiles: File[];
}

const initialState: ChatState = {
  messages: [],
  chatHistory: [],
  currentSessionId: null,
  isTyping: false,
  streamingMessage: '',
  userProfile: null,
  jobNumber: null,
  uploadedFiles: [],
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    addUserMessage: (state, action: PayloadAction<{ content: string; files?: File[] }>) => {
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        content: action.payload.content,
        type: 'user',
        bot: false,
        timestamp: new Date().toISOString(),
      };
      state.messages.push(userMessage);
      if (action.payload.files) {
        state.uploadedFiles = action.payload.files;
      }
    },
    addBotMessage: (state, action: PayloadAction<{ content: string; metadata?: any; id?: string; type?: string; bot?: boolean; timestamp?: string }>) => {
      const botMessage: ChatMessage = {
        id: action.payload.id || Date.now().toString(),
        content: action.payload.content,
        type: 'assistant',
        bot: true,
        timestamp: action.payload.timestamp || new Date().toISOString(),
        metadata: action.payload.metadata,
      };
      state.messages.push(botMessage);
    },
    setTyping: (state, action: PayloadAction<boolean>) => {
      state.isTyping = action.payload;
    },
    setStreamingMessage: (state, action: PayloadAction<string>) => {
      state.streamingMessage = action.payload;
    },
    clearChat: (state) => {
      state.messages = [];
      state.streamingMessage = '';
      state.isTyping = false;
      state.uploadedFiles = [];
      state.currentSessionId = null;
      state.jobNumber = null;
    },
    setUserProfile: (state, action: PayloadAction<{ id: string; name: string }>) => {
      state.userProfile = action.payload;
    },
    setJobNumber: (state, action: PayloadAction<string>) => {
      state.jobNumber = action.payload;
    },
    clearUploadedFiles: (state) => {
      state.uploadedFiles = [];
    },
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
    },
    updateMessageFeedback: (
      state,
      action: PayloadAction<{ messageId: string; reaction?: 'like' | 'dislike' | ''; comment?: string }>
    ) => {
      const { messageId, reaction, comment } = action.payload;
      const message = state.messages.find((m) => m.id === messageId);
      if (message) {
        if (!message.metadata) {
          message.metadata = {};
        }
        if (reaction !== undefined) {
          message.metadata.reaction = reaction;
        }
        if (comment !== undefined) {
          message.metadata.comment = comment;
        }
      }
    },
  },
});

export const {
  addMessage,
  addUserMessage,
  addBotMessage,
  setTyping,
  setStreamingMessage,
  clearChat,
  setUserProfile,
  setJobNumber,
  clearUploadedFiles,
  setMessages,
  updateMessageFeedback,
} = chatSlice.actions;

export default chatSlice.reducer;
